import React from 'react';
import { ImageIcon, X, Calendar } from 'lucide-react';
import type { NewsArticleWithRelations as NewsArticle, NewsCategory, NewsTag } from '../../types/news';
import ImageWithDimensions from '../cms/ImageWithDimensions';
import { Modal, ModalFooter, ModalButton } from '../shared/Modal';

interface NewsArticleFormProps {
  article: NewsArticle;
  onSave: () => void;
  onClose: () => void;
  onChange: (article: NewsArticle) => void;
  onImageBrowse: () => void;
  isOpen: boolean;
  categories: NewsCategory[];
  tags: NewsTag[];
  selectedTags: NewsTag[];
  onTagsChange: (tags: NewsTag[]) => void;
}

export const NewsArticleForm: React.FC<NewsArticleFormProps> = ({
  article,
  onSave,
  onClose,
  onChange,
  onImageBrowse,
  isOpen,
  categories,
  tags,
  selectedTags,
  onTagsChange
}) => {
  const handleTagToggle = (tag: NewsTag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    const newTags = isSelected
      ? selectedTags.filter(t => t.id !== tag.id)
      : [...selectedTags, tag];
    onTagsChange(newTags);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={article.id ? 'Editar Notícia' : 'Adicionar Notícia'}
      maxWidth="4xl"
      footer={
        <ModalFooter>
          <ModalButton onClick={onClose}>
            Cancelar
          </ModalButton>
          <ModalButton onClick={onSave} variant="primary">
            {article.id ? 'Salvar' : 'Adicionar'}
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {/* Informações Principais e Media */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-primary/80">Título</label>
              <input
                type="text"
                value={article.title || ''}
                onChange={(e) => onChange({ ...article, title: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors text-sm"
                autoFocus
              />
            </div>

            <div>
              <label className="text-sm font-medium text-primary/80">Categoria</label>
              <select
                value={article.category_id || ''}
                onChange={(e) => onChange({ ...article, category_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors text-sm"
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-primary/80">Data de Publicação</label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary/60" />
                <input
                  type="datetime-local"
                  value={article.published_at ? new Date(article.published_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => onChange({ ...article, published_at: e.target.value })}
                  className="w-full pl-10 pr-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors text-sm"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-sm font-medium text-primary/80">Imagem de Capa</label>
            <div className="flex items-start gap-4">
              {article.cover_image ? (
                <div className="relative group flex-1 aspect-video bg-secondary/30 rounded-lg overflow-hidden">
                  <ImageWithDimensions
                    src={article.cover_image}
                    alt={article.title || ''}
                    className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <button
                    onClick={() => onChange({ ...article, cover_image: null })}
                    className="absolute bottom-2 right-2 p-1.5 bg-destructive text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={onImageBrowse}
                  className="flex-1 aspect-video flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary/20 hover:border-primary/40 bg-secondary/10 hover:bg-secondary/20 transition-all duration-300 group"
                >
                  <ImageIcon className="w-8 h-8 text-primary/40 group-hover:text-primary/60 transition-colors" />
                  <span className="text-xs text-primary/40 group-hover:text-primary/60 transition-colors">Selecionar Imagem</span>
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/30 cursor-pointer">
                <input
                  type="checkbox"
                  checked={article.is_featured || false}
                  onChange={(e) => onChange({ ...article, is_featured: e.target.checked })}
                  className="w-4 h-4 rounded border-primary/20 text-primary focus:ring-primary"
                />
                <span className="text-sm">Destaque</span>
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/30 cursor-pointer">
                <input
                  type="checkbox"
                  checked={article.is_breaking || false}
                  onChange={(e) => onChange({ ...article, is_breaking: e.target.checked })}
                  className="w-4 h-4 rounded border-primary/20 text-primary focus:ring-primary"
                />
                <span className="text-sm">Última Hora</span>
              </label>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-primary/80">Resumo</label>
            <textarea
              value={article.excerpt || ''}
              onChange={(e) => onChange({ ...article, excerpt: e.target.value })}
              rows={2}
              className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors resize-none text-sm"
              placeholder="Um breve resumo da notícia..."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-primary/80">Conteúdo</label>
            <textarea
              value={article.content || ''}
              onChange={(e) => onChange({ ...article, content: e.target.value })}
              rows={6}
              className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors resize-none text-sm"
              placeholder="Conteúdo completo da notícia..."
            />
          </div>
        </div>

        {/* Tags e Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-primary/80">Tags</label>
            <div className="mt-1 flex flex-wrap gap-1.5 p-2 bg-secondary/30 rounded-lg border border-primary/10 min-h-[80px]">
              {tags.map((tag) => {
                const isSelected = selectedTags.some(t => t.id === tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-primary/80">Status</label>
            <select
              value={article.status || 'draft'}
              onChange={(e) => onChange({ ...article, status: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors text-sm"
            >
              <option value="draft">Rascunho</option>
              <option value="published">Publicado</option>
              <option value="archived">Arquivado</option>
            </select>
          </div>
        </div>

        {/* SEO */}
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium text-primary/80">Meta Título</label>
            <input
              type="text"
              value={article.meta_title || ''}
              onChange={(e) => onChange({ ...article, meta_title: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors text-sm"
              placeholder="Título otimizado para SEO"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-primary/80">Meta Descrição</label>
            <textarea
              value={article.meta_description || ''}
              onChange={(e) => onChange({ ...article, meta_description: e.target.value })}
              rows={2}
              className="mt-1 w-full px-3 py-2 bg-secondary/50 rounded-lg focus:ring-1 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors resize-none text-sm"
              placeholder="Descrição otimizada para SEO"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};